import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, RadarChart, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, Radar, ComposedChart, Scatter
} from 'recharts';
import {  
  Typography, 
  Grid, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  LinearProgress,
  Box,
  Divider,
  Container,
  Card,
  CardContent
} from '@mui/material';
import {
  HourglassEmpty as HourglassEmptyIcon,
  Loop as LoopIcon,
  Assessment as AssessmentIcon,
  DoneAll as DoneAllIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon, 
  CheckCircle as CheckCircleIcon,
  EmojiEvents as EmojiEventsIcon ,
FormatListNumbered as FormatListNumberedIcon,
Warning,
PeopleAlt,

} from "@mui/icons-material"

import {makeStyles} from '@mui/styles'
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const useStyles = makeStyles((theme) => ({
  chartTitle: {
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  progressContainer: {
    marginTop: theme.spacing(2),
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  tableContainer: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    maxHeight: 400,
    overflow: 'auto',
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  sectionTitle: {
    margin: theme.spacing(3, 0, 2),
    color: theme.palette.primary.dark,
  },
}));

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

// Helper functions
const getStatusName = (statusCode) => {
  const statusMap = {
    1: 'To Do',
    2: 'In Progress',
    // 3: 'In Review',
    3: 'Done',
    5: 'Blocked',
    14: 'Other'
  };
  return statusMap[statusCode] || `Status ${statusCode}`;
};

const getTypeName = (typeCode) => {
  const typeMap = {
    1: 'Bug',
    2: 'Epic',
    3: 'Story',
    4: 'Task',
  };
  return typeMap[typeCode] || `Type ${typeCode}`;
};

const getPriorityName = (priorityCode) => {
  const priorityMap = {
    1: 'Highest',
    2: 'High',
    3: 'Medium',
    4: 'Low',
    5: 'Lowest',
  };
  return priorityMap[priorityCode] || `Type ${priorityCode}`;
};

// Burndown Chart Component
const BurndownChart = ({ data }) => {
  const classes = useStyles();
  
  return (
    <>
    <Box>
       <Grid container spacing={2} sx={{ mb: 2 }}>
  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ border: "2px solid", borderColor: "grey.300", boxShadow: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center',justifyContent:"space-between", mb: 1 }}>
          <Typography variant="h5" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
            {data.issues.length}
          </Typography>
          <AssignmentIcon sx={{ fontSize: 40, color: '#1976d2', mr: 2 }} />
        </Box>
        <Typography color="#1976d2">Total Issues</Typography>
      </CardContent>
    </Card>
  </Grid>

  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ border: "2px solid", borderColor: "grey.300",justifyContent:"space-between", boxShadow: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center',justifyContent:"space-between", mb: 1 }}>
          <Typography variant="h5" sx={{ color: '#f57c00', fontWeight: 'bold' }}>
            {data.issues.filter(issue => getStatusName(issue.status) === 'To Do').length}
          </Typography>
          <HourglassEmptyIcon sx={{ fontSize: 40, color: '#f57c00', mr: 2 }} />
          
        </Box>
        <Typography color="#f57c00">To Do</Typography>
      </CardContent>
    </Card>
  </Grid>

  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ border: "2px solid", borderColor: "grey.300", boxShadow: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent:"space-between",mb: 1 }}>
          <Typography variant="h5" sx={{ color: '#0288d1', fontWeight: 'bold' }}>
            {data.issues.filter(issue => getStatusName(issue.status) === 'In Progress').length}
          </Typography>
          <LoopIcon sx={{ fontSize: 40, color: '#0288d1', mr: 2 }} />
          
        </Box>
        <Typography color="#0288d1">In Progress</Typography>
      </CardContent>
    </Card>
  </Grid>

  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ border: "2px solid", borderColor: "grey.300", boxShadow: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center',justifyContent:"space-between", mb: 1 }}>
          <Typography variant="h5" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
            {data.issues.filter(issue => getStatusName(issue.status) === 'Done').length}
          </Typography>
          <CheckCircleIcon sx={{ fontSize: 40, color: '#2e7d32', mr: 2 }} />
          
        </Box>
        <Typography color="#2e7d32">Done</Typography>
      </CardContent>
    </Card>
  </Grid>
</Grid>
    </Box>
    <Container maxWidth="lg" sx={{ mt: 2, paddingTop:4, mb: 4,border: "2px solid", borderColor: "grey.300", boxShadow: 4 }}>
     <Container sx={{ mt: 2, paddingTop:4, mb: 4,border: "2px solid", borderColor: "grey.100", boxShadow: 1 }}>
     <Typography variant="h6" align="center">
        {data.sprint} Burndown Chart
      </Typography>
      <Typography variant="subtitle1" align="center" gutterBottom>
        {new Date(data.startDate).toLocaleDateString()} - {new Date(data.endDate).toLocaleDateString()}
      </Typography>
      
      <ResponsiveContainer width="100%" height={400}>
        <AreaChart
          data={data.burnData}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Area type="monotone" dataKey="ideal" stackId="1" stroke="#8884d8" fill="#8884d8" name="Ideal" />
          <Area type="monotone" dataKey="actual" stackId="2" stroke="#82ca9d" fill="#82ca9d" name="Actual" />
        </AreaChart>
      </ResponsiveContainer>
</Container>
      <Typography variant="h6" className={classes.sectionTitle} style={{ marginTop: '30px' }}>
  Issue Details
</Typography>
<TableContainer component={Paper} className={classes.tableContainer}>
  <Table stickyHeader>
    <TableHead>
      <TableRow>
        <TableCell align='center'>Sl.No</TableCell>
        <TableCell align='center'>Summary</TableCell>
        <TableCell align='center'>Assignee</TableCell>
        <TableCell align='center'>Priority</TableCell>
        <TableCell align='center'>Story Points</TableCell>
        <TableCell align='center'>Status</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {data.issues.map((issue, index) => (
        <TableRow key={issue.id}>
          <TableCell align='center'>{index+1}</TableCell>
          <TableCell align='center'>{issue.summary}</TableCell>
          <TableCell align='center'>
            {issue.assignee || 'Unassigned'}
            {/* {issue.assigneeName?.fullName ||
             (issue.assigneeName?.firstName ? `${issue.assigneeName.firstName} ${issue.assigneeName.lastName}` : 'Unassigned')} */}
          </TableCell>
          <TableCell align='center'>{getPriorityName(issue.priority)}</TableCell>
          <TableCell align='center'>{issue.storyPoints || 0}</TableCell>
          <TableCell align='center'>{getStatusName(issue.status)}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
</TableContainer>
      
      </Container>
      
    </>
  );
};

// Velocity Chart Component
const VelocityChart = ({ data }) => {
  const classes = useStyles();
  const totalSprints = data.length;
const totalCompletedPoints = data.reduce((sum, row) => sum + row.completedPoints, 0);
const totalStoryPoints = data.reduce((sum, row) => sum + row.totalPoints, 0);
const avgCompletion = totalStoryPoints > 0 ? Math.round((totalCompletedPoints / totalStoryPoints) * 100) : 0;

  return (
    <>
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ border: "2px solid", borderColor: "grey.300", boxShadow: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center',justifyContent:"space-between", mb: 1 }}>
          <Typography variant="h5" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
            {totalSprints}
          </Typography>
          <AssessmentIcon sx={{ fontSize: 40, color: '#1976d2', mr: 2 }} />
        </Box>
        <Typography color="#1976d2">Total Sprints</Typography>
      </CardContent>
    </Card>
  </Grid>

  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ border: "2px solid", borderColor: "grey.300", boxShadow: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center',justifyContent:"space-between", mb: 1 }}>
          <Typography variant="h5" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
            {totalCompletedPoints}
          </Typography>
          <DoneAllIcon sx={{ fontSize: 40, color: '#4CAF50', mr: 2 }} />
          
        </Box>
        <Typography color="#4CAF50">Completed Points</Typography>
      </CardContent>
    </Card>
  </Grid>

  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ border: "2px solid", borderColor: "grey.300", boxShadow: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center',justifyContent:"space-between", mb: 1 }}>
          <Typography variant="h5" sx={{ color: '#FF9800', fontWeight: 'bold' }}>
            {totalStoryPoints}
          </Typography>
          <PlaylistAddCheckIcon sx={{ fontSize: 40, color: '#FF9800', mr: 2 }} />
          
        </Box>
        <Typography color="#FF9800">Total Story Points</Typography>
      </CardContent>
    </Card>
  </Grid>

  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ border: "2px solid", borderColor: "grey.300", boxShadow: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center',justifyContent:"space-between", mb: 1 }}>
          <Typography variant="h5" sx={{ color: '#9C27B0', fontWeight: 'bold' }}>
            {avgCompletion}%
          </Typography>
          <TrendingUpIcon sx={{ fontSize: 40, color: '#9C27B0', mr: 2 }} />
          
        </Box>
        <Typography color="#9C27B0">Avg. Completion</Typography>
      </CardContent>
    </Card>
  </Grid>
</Grid>
    </Box>
<Container sx={{ mt: 2, paddingTop:4, mb: 4,border: "2px solid", borderColor: "grey.100", boxShadow: 1 }}>
    <Typography variant="h6" align="center">
        Team Velocity
      </Typography>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="sprintName" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="completedPoints" fill="#8884d8" name="Completed Points" />
          <Bar dataKey="totalPoints" fill="#82ca9d" name="Total Points" />
        </BarChart>
      </ResponsiveContainer>
      </Container>
    <Typography variant="h6" className={classes.sectionTitle}>
        Velocity Data by Sprint
      </Typography>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table  stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell >Sprint</TableCell>
              <TableCell  >Start Date</TableCell>
              <TableCell  >End Date</TableCell>
              <TableCell  align='center'>Completed Points</TableCell>
              <TableCell  align='center'>Total Story Points</TableCell>
              <TableCell  align='center'>Completion %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow key={index} >
                <TableCell>{row.sprintName}</TableCell>
                <TableCell >{new Date(row.startDate).toLocaleDateString()}</TableCell>
                <TableCell >{new Date(row.endDate).toLocaleDateString()}</TableCell>
                <TableCell align='center'>{row.completedPoints}</TableCell>
                <TableCell align='center'>{row.totalPoints}</TableCell>
                <TableCell align='center' >
                  {Math.round((row.completedPoints / row.totalPoints) * 100)}%
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      
      
    </>
  );
};

// Issue Distribution Component
const IssueDistributionChart = ({ data, type }) => {
  const classes = useStyles();
  
  const chartData = data.map(item => ({
    name: type === 'status'
      ? getStatusName(item.status)
      : type === 'type'
      ? getTypeName(item.issueType)
      : item.priority,
    value: Number(item.count),
    points: Number(item.totalPoints || 0)
  }));

  return (
    <>
    <Typography variant="h6" className={classes.sectionTitle}>
        {type === 'status' ? 'Status Distribution Details' : 
         type === 'type' ? 'Type Distribution Details' : 'Priority Distribution Details'}
      </Typography>
      <TableContainer component={Paper} >
        <Table  stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align='center'>
                {type === 'status' ? 'Status' : type === 'type' ? 'Type' : 'Priority'}
              </TableCell>
              <TableCell align='center'>Count</TableCell>
              {/* <TableCell>Percentage</TableCell> */}
              {type !== 'type' && (
                <TableCell align='center'>Total Points</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {chartData.map((row, index) => (
              <TableRow key={index} >
                <TableCell align='center'>{row.name}</TableCell>
                <TableCell align='center'>{row.value}</TableCell>
                {/* <TableCell align="right">
                  {Math.round((row.value / chartData.reduce((a, b) => a + b.value, 0)) * 100)}%
                </TableCell> */}
                {type !== 'type' && (
                  <TableCell >{row.points || '-'}</TableCell>
                )}
              </TableRow>
            ))}
            <TableRow>
              <TableCell align='center'><strong>Total</strong></TableCell>
              <TableCell align='center' ><strong>{chartData.reduce((a, b) => a + b.value, 0)}</strong></TableCell>
              {/* <TableCell align="right"><strong>100%</strong></TableCell> */}
              {type !== 'type' && (
                <TableCell>
                  <strong>{chartData.reduce((a, b) => a + (b.points || 0), 0)}</strong>
                </TableCell>
              )}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="h6" align="center">
        {type === 'status' ? 'Issue Status Distribution' : 
         type === 'type' ? 'Issue Type Distribution' : 'Issue Priority Distribution'}
      </Typography>
      
      <ResponsiveContainer width="100%" height={400}>
        {type === 'status' || type === 'type' ? (
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={120}
              fill="#8884d8"
              dataKey="value"
              nameKey="name"
              label={({ name,value, percent }) => `${name}: ${value}`}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        ) : (
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="value" fill="#8884d8" name="Count" />
          </BarChart>
        )}
      </ResponsiveContainer>
      
      
    </>
  );
};

// Sprint Progress Component
const SprintProgress = ({ data }) => {
  const classes = useStyles();
  
  const assigneeData = Object.keys(data.assigneeDistribution).map(key => ({
    name: data.assigneeDistribution[key].assigneeName||data.assigneeDistribution[key].assigneeId||"unassigned",
    completed: data.assigneeDistribution[key].completed,
    inProgress: data.assigneeDistribution[key].inProgress,
    todo: data.assigneeDistribution[key].todo
  }));

  return (
    <>
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ border: "2px solid", borderColor: "grey.300", boxShadow: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 , justifyContent:"space-between"}}>
          <Typography variant="h5" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
            {data.totalIssues}
          </Typography>
          <AssignmentIcon sx={{ fontSize: 40, color: '#1976d2', mr: 2 }} />
          
        </Box>
        <Typography color="#1976d2">Total Issues</Typography>
      </CardContent>
    </Card>
  </Grid>

  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ border: "2px solid", borderColor: "grey.300", boxShadow: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center',justifyContent:"space-between", mb: 1 }}>
          <Typography variant="h5" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
            {data.completedIssues}
          </Typography>
          <CheckCircleIcon sx={{ fontSize: 40, color: '#4CAF50', mr: 2 }} />
          
        </Box>
        <Typography color="#4CAF50">Completed Issues</Typography>
      </CardContent>
    </Card>
  </Grid>

  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ border: "2px solid", borderColor: "grey.300", boxShadow: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent:"space-between",mb: 1 }}>
          <Typography variant="h5" sx={{ color: '#FF9800', fontWeight: 'bold' }}>
            {data.totalStoryPoints}
          </Typography>
          <FormatListNumberedIcon sx={{ fontSize: 40, color: '#FF9800', mr: 2 }} />
          
        </Box>
        <Typography color="#FF9800">Total Story Points</Typography>
      </CardContent>
    </Card>
  </Grid>

  <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ border: "2px solid", borderColor: "grey.300", boxShadow: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center',justifyContent:"space-between", mb: 1 }}>
          <Typography variant="h5" sx={{ color: '#9C27B0', fontWeight: 'bold' }}>
            {data.completedPoints}
          </Typography>
          <EmojiEventsIcon sx={{ fontSize: 40, color: '#9C27B0', mr: 2 }} />
          
        </Box>
        <Typography color="#9C27B0">Completed Story Points</Typography>
      </CardContent>
    </Card>
  </Grid>
</Grid>
    </Box>
    <Container maxWidth="lg" sx={{ mt: 2,paddingBottom:6, paddingTop:4, mb: 4,border: "2px solid", borderColor: "grey.300", boxShadow: 4 }}>
    <Typography variant="h6" align="center">
        {data.sprintName} Progress
      </Typography>
      <Typography variant="subtitle1" align="center" gutterBottom>
        {new Date(data.startDate).toLocaleDateString()} - {new Date(data.endDate).toLocaleDateString()}
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} marginLeft={27}>
          <Paper elevation={3} style={{ padding: 16, }}>
            <Typography variant="subtitle1" gutterBottom>
              Overall Progress
            </Typography>
            <div className={classes.progressContainer}>
              <div className={classes.progressLabel}>
                <Typography variant="body2">
                  {data.completedIssues} of {data.totalIssues} issues completed
                </Typography>
                <Typography variant="body2">
                  {Math.round(data.completionPercentage)}%
                </Typography>
              </div>
              <LinearProgress 
                variant="determinate" 
                value={data.completionPercentage} 
                color="primary"
              />
            </div>
            
            <div className={classes.progressContainer} style={{ marginTop: 24, marginBottom: 14 }}>
              <div className={classes.progressLabel}>
                <Typography variant="body2">
                  {data.completedPoints} of {data.totalStoryPoints} story points completed
                </Typography>
                <Typography variant="body2">
                  {Math.round(data.pointsCompletionPercentage)}%
                </Typography>
              </div>
              <LinearProgress 
                variant="determinate" 
                value={data.pointsCompletionPercentage} 
                color="secondary"
              />
            </div>
          </Paper>
        </Grid>
        
        {/* <Grid item xs={12} md={6}>
          <Paper elevation={3} style={{ padding: 16 }}>
            <Typography variant="subtitle1" gutterBottom>
              Work Distribution by Assignee
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={assigneeData}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                stackOffset="expand"
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis dataKey="name" type="category" width={100} />
                <Tooltip />
                <Legend />
                <Bar dataKey="completed" stackId="a" fill="#4CAF50" name="Completed" />
                <Bar dataKey="inProgress" stackId="a" fill="#FFC107" name="In Progress" />
                <Bar dataKey="todo" stackId="a" fill="#F44336" name="To Do" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid> */}
      </Grid>

    <Typography variant="h6" className={classes.sectionTitle}>
        Detailed Work Distribution
      </Typography>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table  stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell >Assignee</TableCell>
              <TableCell  align="center">Completed</TableCell>
              <TableCell  align="center">In Progress</TableCell>
              <TableCell  align="center">To Do</TableCell>
              <TableCell  align="center">Total</TableCell>
              <TableCell  align="center">Completion %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assigneeData.map((row, index) => (
              <TableRow key={index} >
                <TableCell>{row.name}</TableCell>
                <TableCell align="center">{row.completed}</TableCell>
                <TableCell align="center">{row.inProgress}</TableCell>
                <TableCell align="center">{row.todo}</TableCell>
                <TableCell align="center">{row.completed + row.inProgress + row.todo}</TableCell>
                <TableCell align="center">
                  {Math.round((row.completed / (row.completed + row.inProgress + row.todo)) * 100)}%
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      </Container>
      
    </>
  );
};

const ResourceUtilizationChart = ({ data }) => {
  const classes = useStyles();
  
  const getColor = (value) => {
    if (value < 100) return "#FFC107";  // Underutilized - Yellow
    if (value === 100) return "#4CAF50"; // Fully utilized - Green
    return "#F44336"; // Overutilized - Red
  };
  const totalResources = data.length;
const fullyUtilized = data.filter(r => r.totalAllocation === 100).length;
const underUtilized = data.filter(r => r.totalAllocation < 100).length;
const overUtilized = data.filter(r => r.totalAllocation > 100).length;


  return (
    <>
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
  <Grid item xs={12} sm={4} >
    <Card sx={{ border: "2px solid", borderColor: "grey.300", boxShadow: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center',justifyContent:"space-between", mb: 1 }}>
          <Typography variant="h5" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
            {totalResources}
          </Typography>
          <PeopleAlt sx={{ fontSize: 40, color: '#1976d2', mr: 2 }} />
          
        </Box>
        <Typography color="#1976d2">Total Resources</Typography>
      </CardContent>
    </Card>
  </Grid>

  <Grid item xs={12} sm={4} >
    <Card sx={{ border: "2px solid", borderColor: "grey.300", boxShadow: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1,justifyContent:"space-between",}}>
          <Typography variant="h5" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
            {fullyUtilized}
          </Typography>
          <CheckCircleIcon sx={{ fontSize: 40, color: '#4CAF50', mr: 2 }} />
          
        </Box>
        <Typography color="#4CAF50">Fully Utilized</Typography>
      </CardContent>
    </Card>
  </Grid>

  <Grid item xs={12} sm={4} >
    <Card sx={{ border: "2px solid", borderColor: "grey.300", boxShadow: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center',justifyContent:"space-between", mb: 1 }}>
          <Typography variant="h5" sx={{ color: '#FFC107', fontWeight: 'bold' }}>
            {underUtilized}
          </Typography>
          <Warning sx={{ fontSize: 40, color: '#FFC107', mr: 2 }} />
          
        </Box>
        <Typography color="#FFC107">Under Utilized</Typography>
      </CardContent>
    </Card>
  </Grid>

  {/* <Grid item xs={12} sm={6} md={3}>
    <Card sx={{ border: "2px solid", borderColor: "grey.300", boxShadow: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <ErrorIcon sx={{ fontSize: 40, color: '#F44336', mr: 2 }} />
          <Typography variant="h5" sx={{ color: '#F44336', fontWeight: 'bold' }}>
            {overUtilized}
          </Typography>
        </Box>
        <Typography color="#F44336">Over Utilized</Typography>
      </CardContent>
    </Card>
  </Grid>*/}
</Grid> 
      </Box>

<Container sx={{ mt: 2, paddingTop:4, mb: 4,border: "2px solid", borderColor: "grey.100", boxShadow: 1 }}>

    <Typography variant="h6" align="center" gutterBottom>
        Resource Utilization (Project Level)
      </Typography>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart 
          data={data} 
          margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" angle={-30} textAnchor="end" interval={0} height={100} />
          <YAxis domain={[0, 150]} tickFormatter={(tick) => `${tick}%`} />
          <Tooltip formatter={(value) => `${value}%`} />
          <Legend />
          <Bar dataKey="totalAllocation" name="Utilization %" isAnimationActive={false}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getColor(entry.totalAllocation)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      </Container>
     <Typography variant="h6" className={classes.sectionTitle}>
        Resource Utilization Details
      </Typography>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align='center'>Resource Name</TableCell>
              <TableCell align='center' >Type</TableCell>
              <TableCell align='center' >Utilization %</TableCell>
              <TableCell align='center' >Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
  {data
    .filter((row) => row.totalAllocation < 100) // 👈 only show under-utilized
    .map((row, index) => (
      <TableRow key={index}>
        <TableCell align='center'>{row.name}</TableCell>
        <TableCell align='center'>{row.type}</TableCell>
        <TableCell align='center'>{row.totalAllocation}%</TableCell>
        <TableCell align='center'>
          <span
            style={{
              color: getColor(row.totalAllocation),
              fontWeight: 'bold',
            }}
          >
            {row.totalAllocation < 100
              ? 'Under Utilized'
              : row.totalAllocation === 100
              ? 'Fully Utilized'
              : 'Over Utilized'}
          </span>
        </TableCell>
      </TableRow>
    ))}
</TableBody>

        </Table>
      </TableContainer>
      
      
     
    </>
  );
};

export {
  BurndownChart,
  VelocityChart,
  IssueDistributionChart,
  SprintProgress,
  ResourceUtilizationChart
};